import React, { ReactElement } from "react";
import InstallAppCta from "./installTheAppCta";
import GoUnlimitedCta from "./goUnlimitedCta";
import ReferFriendPaidCta from "./referFriendPaidCta";
import SignupSocialSidebarCta from "./signupSocialCta";
import ReferFriendFreeCta from "./referFriendFreeCta";
import { LoadingSkeleton } from "@kunduz/ui";
import { useAppContext } from "@kunduz/feature-website-context";
import { SideCTAModes } from "./index";
import { useTranslation } from "next-i18next";
import type { QuestionIndexing } from "@kunduz/data-access-qi-api";

const QuestionSideCta = (props: Props): ReactElement => {
  const { student } = useAppContext();
  const { t } = useTranslation(props.translationFile);

  const translationKeys = {
    InstallAppCta: {
      [SideCTAModes.BLOG]: {
        title: t("blog_sidebar_install_component_title"),
        qrLabel: t("blog_sidebar_install_component_qr_label"),
        firstBenefit: t("blog_sidebar_install_component_first_benefit"),
        secondBenefit: t("blog_sidebar_install_component_second_benefit"),
        thirdBenefit: t("blog_sidebar_install_component_third_benefit"),
        fourthBenefit: t("blog_sidebar_install_component_fourth_benefit"),
        appStoreLink: t("blog_install_redirection_banner"),
        playStoreLink: t("blog_install_redirection_banner"),
        qrCodeLink: t("blog_install_redirection_banner"),
      },
      [SideCTAModes.LANDING]: {
        title: t("blog_sidebar_install_component_title"),
        qrLabel: t("blog_sidebar_install_component_qr_label"),
        firstBenefit: t("blog_sidebar_install_component_first_benefit"),
        secondBenefit: t("blog_sidebar_install_component_second_benefit"),
        thirdBenefit: t("blog_sidebar_install_component_third_benefit"),
        fourthBenefit: t("blog_sidebar_install_component_fourth_benefit"),
        appStoreLink: t("question_indexing_install_redirection_banner"),
        playStoreLink: t("question_indexing_install_redirection_banner"),
        qrCodeLink: t("question_indexing_install_redirection_banner"),
      },
      [SideCTAModes.LEARNING]: {
        title: t("learning_sidebar_install_component_title"),
        qrLabel: t("learning_sidebar_install_component_qr_label"),
        firstBenefit: t("learning_sidebar_install_component_first_benefit"),
        secondBenefit: t("learning_sidebar_install_component_second_benefit"),
        thirdBenefit: t("learning_sidebar_install_component_third_benefit"),
        fourthBenefit: t("learning_sidebar_install_component_fourth_benefit"),
        appStoreLink: t("question_indexing_install_redirection_banner"),
        playStoreLink: t("question_indexing_install_redirection_banner"),
        qrCodeLink: t("question_indexing_install_redirection_banner"),
      },
      [SideCTAModes.QI]: {
        title: t("question_indexing_sidebar_install_component_title"),
        qrLabel: t("question_indexing_sidebar_install_component_qr_label"),
        firstBenefit: t(
          "question_indexing_sidebar_install_component_first_benefit",
        ),
        secondBenefit: t(
          "question_indexing_sidebar_install_component_second_benefit",
        ),
        thirdBenefit: t(
          "question_indexing_sidebar_install_component_third_benefit",
        ),
        fourthBenefit: t(
          "question_indexing_sidebar_install_component_fourth_benefit",
        ),
        appStoreLink: t("question_indexing_install_redirection_banner"),
        playStoreLink: t("question_indexing_install_redirection_banner"),
        qrCodeLink: t("question_indexing_install_redirection_banner"),
      },
    },
    signupSocialSidebarCta: {
      [SideCTAModes.BLOG]: {
        propositionTitle: t(
          t("blog_sidebar_component_signup_state_head_proposition_text"),
        ),
        propositionText1: t(
          "blog_sidebar_component_signup_state_first_value_proposition_text",
        ),
        propositionText2: t(
          "blog_sidebar_component_signup_state_second_value_proposition_text",
        ),
        propositionText3: t(
          "blog_sidebar_component_signup_state_third_value_proposition_text",
        ),
        propositionText4: t(
          "blog_sidebar_component_signup_state_fourth_value_proposition_text",
        ),
        mobileTitle: t("blog_mobile_signup_banner_title"),
        mobileSubtitle: t("blog_mobile_signup_banner_subtitle"),
      },
      [SideCTAModes.LANDING]: {
        propositionTitle: t(
          t("blog_sidebar_component_signup_state_head_proposition_text"),
        ),
        propositionText1: t(
          "blog_sidebar_component_signup_state_first_value_proposition_text",
        ),
        propositionText2: t(
          "blog_sidebar_component_signup_state_second_value_proposition_text",
        ),
        propositionText3: t(
          "blog_sidebar_component_signup_state_third_value_proposition_text",
        ),
        propositionText4: t(
          "blog_sidebar_component_signup_state_fourth_value_proposition_text",
        ),
        mobileTitle: t("blog_mobile_signup_banner_title"),
        mobileSubtitle: t("blog_mobile_signup_banner_subtitle"),
      },
      [SideCTAModes.LEARNING]: {
        propositionTitle: t(
          "learning_sidebar_component_signup_state_head_proposition_text",
        ),
        propositionText1: t(
          "learning_sidebar_component_signup_state_bullet_proposition_text_1",
        ),
        propositionText2: t(
          "learning_sidebar_component_signup_state_bullet_proposition_text_2",
        ),
        propositionText3: t(
          "learning_sidebar_component_signup_state_bullet_proposition_text_3",
        ),
        propositionText4: t(
          "learning_sidebar_component_signup_state_bullet_proposition_text_4",
        ),
        mobileTitle: t("learning_mobile_signup_banner_title"),
        mobileSubtitle: t("learning_mobile_signup_banner_subtitle"),
      },
      [SideCTAModes.QI]: {
        propositionTitle: t(
          t(
            "question_indexing_sidebar_component_signup_state_head_proposition_text",
          ),
        ),
        propositionText1: t(
          "question_indexing_sidebar_component_signup_state_first_value_proposition_text",
        ),
        propositionText2: t(
          "question_indexing_sidebar_component_signup_state_second_value_proposition_text",
        ),
        propositionText3: t(
          "question_indexing_sidebar_component_signup_state_third_value_proposition_text",
        ),
        propositionText4: t(
          "question_indexing_sidebar_component_signup_state_fourth_value_proposition_text",
        ),
        mobileTitle: t("question_indexing_mobile_signup_banner_title"),
        mobileSubtitle: t("question_indexing_mobile_signup_banner_subtitle"),
      },
    },
  };

  if (student.loading) {
    return <LoadingSkeleton style={{ width: "100%", height: 500 }} />;
  }

  function printCta() {
    // return (
    //   <>
    //     <SignupSocialSidebarCta
    //       lang={props.lang}
    //       isMobile={props.isMobile}
    //       propositionTitle={
    //         translationKeys.signupSocialSidebarCta[props.mode].propositionTitle
    //       }
    //       propositionText1={
    //         translationKeys.signupSocialSidebarCta[props.mode].propositionText1
    //       }
    //       propositionText2={
    //         translationKeys.signupSocialSidebarCta[props.mode].propositionText2
    //       }
    //       propositionText3={
    //         translationKeys.signupSocialSidebarCta[props.mode].propositionText3
    //       }
    //       propositionText4={
    //         translationKeys.signupSocialSidebarCta[props.mode].propositionText4
    //       }
    //       mobileTitle={
    //         translationKeys.signupSocialSidebarCta[props.mode].mobileTitle
    //       }
    //       mobileSubtitle={
    //         translationKeys.signupSocialSidebarCta[props.mode].mobileSubtitle
    //       }
    //     />
    //     <InstallAppCta
    //       question={props.question}
    //       title={translationKeys.InstallAppCta[props.mode].title}
    //       qrLabel={translationKeys.InstallAppCta[props.mode].qrLabel}
    //       firstBenefit={translationKeys.InstallAppCta[props.mode].firstBenefit}
    //       secondBenefit={
    //         translationKeys.InstallAppCta[props.mode].secondBenefit
    //       }
    //       thirdBenefit={translationKeys.InstallAppCta[props.mode].thirdBenefit}
    //       fourthBenefit={
    //         translationKeys.InstallAppCta[props.mode].fourthBenefit
    //       }
    //     />
    //     <GoUnlimitedCta question={props.question} />
    //     <ReferFriendPaidCta question={props.question} />
    //     <ReferFriendFreeCta question={props.question} />
    //   </>
    // );

    if (!student.data) {
      return (
        <SignupSocialSidebarCta
          lang={props.lang}
          isMobile={props.isMobile}
          propositionTitle={
            translationKeys.signupSocialSidebarCta[props.mode].propositionTitle
          }
          propositionText1={
            translationKeys.signupSocialSidebarCta[props.mode].propositionText1
          }
          propositionText2={
            translationKeys.signupSocialSidebarCta[props.mode].propositionText2
          }
          propositionText3={
            translationKeys.signupSocialSidebarCta[props.mode].propositionText3
          }
          propositionText4={
            translationKeys.signupSocialSidebarCta[props.mode].propositionText4
          }
          mobileTitle={
            translationKeys.signupSocialSidebarCta[props.mode].mobileTitle
          }
          mobileSubtitle={
            translationKeys.signupSocialSidebarCta[props.mode].mobileSubtitle
          }
        />
      );
    } else if (
      student.data?.limits?.questions?.remained > 0 &&
      !student.data?.has_active_device
    ) {
      return (
        <InstallAppCta
          question={props.question}
          title={translationKeys.InstallAppCta[props.mode].title}
          qrLabel={translationKeys.InstallAppCta[props.mode].qrLabel}
          firstBenefit={translationKeys.InstallAppCta[props.mode].firstBenefit}
          secondBenefit={
            translationKeys.InstallAppCta[props.mode].secondBenefit
          }
          thirdBenefit={translationKeys.InstallAppCta[props.mode].thirdBenefit}
          fourthBenefit={
            translationKeys.InstallAppCta[props.mode].fourthBenefit
          }
          appStoreLink={translationKeys.InstallAppCta[props.mode].appStoreLink}
          playStoreLink={
            translationKeys.InstallAppCta[props.mode].playStoreLink
          }
          qrCodeLink={translationKeys.InstallAppCta[props.mode].qrCodeLink}
        />
      );
    } else if (student.data?.limits?.questions?.remained == 0) {
      return <GoUnlimitedCta question={props.question} />;
    } else if (
      student.data?.has_active_device &&
      student.data?.limits?.questions?.remained > 0 &&
      !!student.data?.primary_package
    ) {
      return <ReferFriendPaidCta question={props.question} />;
    } else if (
      student.data?.has_active_device &&
      student.data?.limits?.questions?.remained > 0 &&
      !student.data?.primary_package
    ) {
      return <ReferFriendFreeCta question={props.question} />;
    } else {
      return null;
    }
  }

  return printCta();
};

interface Props {
  isMobile: boolean;
  question?: QuestionIndexing.TrQiQuestion;
  lang: "tr" | "en";
  mode: SideCTAModes;
  translationFile: string;
}
export default QuestionSideCta;
